import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  beginHostedUiSignIn,
  clearSession,
  completeHostedUiSignIn,
  createDemoSession,
  getStoredSession,
  hostedUiLogout
} from '../lib/auth'
import { environment } from '../lib/env'
import type { Role, Session } from '../types'

interface AuthContextValue {
  completeSignIn: (search: string) => Promise<Session>
  demoSignIn: (role: Role) => void
  session: Session | null
  signIn: () => Promise<void>
  signOut: (options?: { returnToIdentityProvider?: boolean }) => void
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(() => getStoredSession())
  const lastActivity = useRef(Date.now())

  const signOut = useCallback((options?: { returnToIdentityProvider?: boolean }) => {
    const shouldUseHostedLogout = options?.returnToIdentityProvider ?? !session?.isDemo
    clearSession()
    setSession(null)
    if (shouldUseHostedLogout) hostedUiLogout()
  }, [session?.isDemo])

  useEffect(() => {
    const onUnauthorized = () => signOut({ returnToIdentityProvider: true })
    window.addEventListener('qte:unauthorized', onUnauthorized)
    return () => window.removeEventListener('qte:unauthorized', onUnauthorized)
  }, [signOut])

  useEffect(() => {
    if (!session) return

    const recordActivity = () => {
      lastActivity.current = Date.now()
    }
    const events: Array<keyof WindowEventMap> = ['mousedown', 'keydown', 'touchstart', 'scroll']
    events.forEach((event) => window.addEventListener(event, recordActivity, { passive: true }))
    const timer = window.setInterval(() => {
      if (Date.now() - lastActivity.current > environment.idleTimeoutSeconds * 1000) {
        signOut({ returnToIdentityProvider: !session.isDemo })
      }
    }, 15_000)

    return () => {
      events.forEach((event) => window.removeEventListener(event, recordActivity))
      window.clearInterval(timer)
    }
  }, [session, signOut])

  const value = useMemo<AuthContextValue>(
    () => ({
      completeSignIn: async (search) => {
        const nextSession = await completeHostedUiSignIn(search)
        setSession(nextSession)
        return nextSession
      },
      demoSignIn: (role) => setSession(createDemoSession(role)),
      session,
      signIn: beginHostedUiSignIn,
      signOut
    }),
    [session, signOut]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside AuthProvider.')
  return context
}
